import { Box, Button, CircularProgress, Divider, Stack } from "@mui/material";

import StockPageHeader from "../../../components/StockPageHeader";
import SalesImportStepIndicator from "./SalesImportStepIndicator";

const secondaryButtonSx = {
  minWidth: 120,
  height: 38,
  px: 2.4,
  borderRadius: "33px",
  bgcolor: "secondary.50",
  color: "primary.900",
  textTransform: "none",
  fontSize: 12,
  fontWeight: 800,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "&:hover": { bgcolor: "secondary.100" },
  "&.Mui-disabled": {
    bgcolor: "secondary.50",
    color: "neutral.500",
    opacity: 0.7,
  },
};

const primaryButtonSx = {
  minWidth: 120,
  height: 38,
  px: 2.4,
  borderRadius: "33px",
  bgcolor: "primary.500",
  color: "logoblanco",
  textTransform: "none",
  fontSize: 12,
  fontWeight: 800,
  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
  "&:hover": { bgcolor: "primary.600" },
  "&.Mui-disabled": {
    bgcolor: "secondary.100",
    color: "neutral.500",
  },
};

const STEP_TITLES = {
  1: "Archivo de ventas",
  2: "Productos y Stock",
  3: "Tesorería",
  4: "Resumen e integración",
};

export default function SalesImportWizardShell({
  leftPanel,
  children,
  currentStep = 1,
  busy = false,
  backDisabled = true,
  continueDisabled = true,
  primaryLabel = "Continuar",
  onExit,
  onCancel,
  onBack,
  onContinue,
}) {
  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        width: "100%",
        minHeight: 0,
        minWidth: 0,
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title={STEP_TITLES[currentStep] || STEP_TITLES[1]}
        onBack={onExit}
        backDisabled={busy}
      />

      <Box
        sx={{
          p: { xs: 2, md: 3 },
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          bgcolor: "neutral.50",
          borderRadius: "24px",
          boxShadow: "inset 0px 0px 3px rgba(117, 117, 138, 0.22)",
          overflowX: "hidden",
          overflowY: { xs: "visible", md: "hidden" },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(220px, 285px) auto minmax(0, 1fr)",
            },
            columnGap: { md: 2 },
            rowGap: { xs: 2.5, md: 3 },
            height: { md: "100%" },
            minHeight: 0,
          }}
        >
          <Box component="aside" aria-label="Archivo y resumen" sx={{ minWidth: 0, minHeight: 0, overflowY: { md: "auto" } }}>
            {leftPanel}
          </Box>

          <Divider sx={{ display: { xs: "block", md: "none" } }} />
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", md: "block" }, borderColor: "neutral.500" }}
          />

          <Stack spacing={2} sx={{ minWidth: 0, minHeight: 0, overflow: { md: "hidden" } }}>
            <Box
              component="section"
              aria-label={`Contenido del paso ${currentStep}`}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflowY: { md: currentStep === 1 || currentStep === 4 ? "auto" : "hidden" },
                overflowX: { md: "hidden" },
                scrollbarGutter: currentStep === 4 ? "stable" : "auto",
                overscrollBehavior: currentStep === 4 ? "contain" : "auto",
              }}
            >
              {children}
            </Box>

            <Stack
              role="group"
              aria-label="Acciones de importación"
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              useFlexGap
              sx={{
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexWrap: "wrap",
                flexShrink: 0,
                px: 0.5,
                pt: 0.25,
                pb: 1.25,
                overflow: "visible",
              }}
            >
              <Button
                type="button"
                variant="contained"
                disabled={busy}
                onClick={onCancel}
                sx={secondaryButtonSx}
              >
                Cancelar
              </Button>

              <SalesImportStepIndicator currentStep={currentStep} />

              <Stack direction="row" spacing={1.25} sx={{ justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  variant="contained"
                  disabled={busy || backDisabled}
                  onClick={onBack}
                  sx={{
                    ...secondaryButtonSx,
                    bgcolor: "secondary.500",
                    color: "logoblanco",
                    "&:hover": { bgcolor: "secondary.600" },
                  }}
                >
                  Atrás
                </Button>
                <Button
                  type="button"
                  disabled={busy || continueDisabled}
                  onClick={onContinue}
                  startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
                  sx={primaryButtonSx}
                >
                  {primaryLabel}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}
